(ns hypnos.translator.patient
  "Translators between Clojure and GraphQL for the patient models."
  (:require [clojure.spec.alpha :as s]
            [clojure.string :as str]
            [hypnos.backoffice.core.catalog :as catalog]
            [hypnos.config :as config]
            [hypnos.time :as t]
            [hypnos.translator.catalog :as t-c]
            [hypnos.translator.communication :as t-com]
            [hypnos.translator.organization :as t-o]
            [medley.core :as mc]))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Specs

;; consult-state
(def ^{:const true :private true} consult-states
  #{:consult-state/new
    :consult-state/in-progress
    :consult-state/pending
    :consult-state/completed
    :consult-state/canceled
    :consult-state/expired})
(s/def ::consult-state consult-states)
(s/def ::consult-state-gql (set (map name consult-states)))

;; communication-channel
(def ^{:const true :private true} communication-channels
  #{:communication-channel/email
    :communication-channel/sms})
(s/def ::communication-channel communication-channels)
(s/def ::communication-channel-gql (set (map name communication-channels)))

;; gender
(def ^{:const true :private true} genders
  #{:gender/female
    :gender/male
    :gender/unknown})
(s/def ::gender genders)
(s/def ::gender-gql (set (map name genders)))

;; modality
(def ^{:const true :private true} modalities
  #{:modality/cpap
    :modality/autopap-smartpap
    :modality/bipap-vpap
    :modality/other
    :modality/unknown})
(s/def ::modality modalities)
(s/def ::modality-gql (set (map name modalities)))

;; sleep-position
(def ^{:const true :private true} sleep-positions
  #{:sleep-position/side
    :sleep-position/back
    :sleep-position/stomach
    :sleep-position/upright
    :sleep-position/unknown})
(s/def ::sleep-position sleep-positions)
(s/def ::sleep-position-gql (set (map name sleep-positions)))

;; issue
(def ^{:const true :private true} issues
  #{:issue/claustrophobic
    :issue/breathing
    :issue/eye
    :issue/nose
    :issue/forehead
    :issue/face})
(s/def ::issue issues)
(s/def ::issue-gql (set (map name issues)))

;; service type
(def ^{:const true :private true} service-types
  #{:service-type/new-setup
    :service-type/exchange
    :service-type/refit
    :service-type/repap
    :service-type/sleep-study
    :service-type/home-study})
(s/def ::service-type service-types)
(s/def ::service-type-gql (set (map name service-types)))

;; selector-type
(def ^{:const true :private true} selector-types
  #{:selector-type/prescriber
    :selector-type/therapist
    :selector-type/unknown})
(s/def ::selector-type selector-types)
(s/def ::selector-type-gql (set (map name selector-types)))

;; timezone
(def ^{:const true :private true} timezones
  #{:timezone/est
    :timezone/est5edt
    :timezone/cst6cdt
    :timezone/mst
    :timezone/mst7mdt
    :timezone/pst
    :timezone/pst8pdt
    :timezone/akdt
    :timezone/hst})
(s/def ::timezone timezones)
(s/def ::timezone-gql (set (map name timezones)))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Helpers

(defn- changed?
  [{:keys [value _dirty? _touched?]}]
  (not (nil? value)))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Enums

(defn consult-state->clj
  [s]
  (when-not (str/blank? s)
    (keyword (str "consult-state/" (str/replace s #"_" "-")))))

(s/fdef consult-state->clj
  :args (s/cat :s (s/or :state ::consult-state-gql :nil nil?))
  :ret (s/or :state ::consult-state :nil nil?))

(defn consult-state->gql
  [o]
  (when (keyword? o)
    (str/replace (name o) #"-" "_")))

(s/fdef consult-state->gql
  :args (s/cat :o (s/or :state ::consult-state :nil nil?))
  :ret (s/or :state ::consult-state-gql :nil nil?))

(defn communication-channel->clj
  [s]
  (when-not (str/blank? s)
    (keyword (str "communication-channel/" s))))

(s/fdef communication-channel->clj
  :args (s/cat :s (s/or :channel ::communication-channel-gql :nil nil?))
  :ret (s/or :channel ::communication-channel :nil nil?))

(defn communication-channel->gql
  [o]
  (when (keyword? o)
    (name o)))

(s/fdef communication-channel->gql
  :args (s/cat :o (s/or :channel ::communication-channel :nil nil?))
  :ret (s/or :channel ::communication-channel-gql :nil nil?))

(defn gender->clj
  [g]
  (when-not (str/blank? g)
    (keyword (str "gender/" g))))

(s/fdef gender->clj
  :args (s/cat :s (s/or :gender ::gender-gql :nil nil?))
  :ret (s/or :gender ::gender :nil nil?))

(defn gender->gql
  [o]
  (when (keyword? o)
    (name o)))

(s/fdef gender->gql
  :args (s/cat :o (s/or :gender ::gender :nil nil?))
  :ret (s/or :gender ::gender-gql :nil nil?))

(defn modality->clj
  [m]
  (when-not (str/blank? m)
    (keyword (str "modality/" (str/replace m #"_" "-")))))

(s/fdef modality->clj
  :args (s/cat :s (s/or :modality ::modality-gql :nil nil?))
  :ret (s/or :modality ::modality :nil nil?))

(defn modality->gql
  [o]
  (when (keyword? o)
    (str/replace (name o) #"-" "_")))

(s/fdef modality->gql
  :args (s/cat :o (s/or :modality ::modality :nil nil?))
  :ret (s/or :modality ::modality-gql :nil nil?))

(defn issue->clj
  [s]
  (when-not (str/blank? s)
    (keyword (str "issue/" s))))

(s/fdef issue->clj
  :args (s/cat :s (s/or :issue ::issue-gql :nil nil?))
  :ret (s/or :issue ::issue :nil nil?))

(defn issue->gql
  [o]
  (when (keyword? o)
    (str/replace (name o) #"-" "_")))

(s/fdef issue->gql
  :args (s/cat :o (s/or :issue ::issue :nil nil?))
  :ret (s/or :issue ::issue-gql :nil nil?))

(defn selector-type->clj
  [s]
  (when-not (str/blank? s)
    (keyword (str "selector-type/" (str/replace s #"_" "-")))))

(s/fdef selector-type->clj
  :args (s/cat :s (s/or :type ::selector-type-gql :nil nil?))
  :ret (s/or :type ::selector-type :nil nil?))

(defn selector-type->gql
  [o]
  (when (keyword? o)
    (str/replace (name o) #"-" "_")))

(s/fdef selector-type->gql
  :args (s/cat :o (s/or :type ::selector-type :nil nil?))
  :ret (s/or :type ::selector-type-gql :nil nil?))

(defn service-type->clj
  [s]
  (when-not (str/blank? s)
    (keyword (str "service-type/" (str/replace s #"_" "-")))))

(s/fdef service-type->clj
  :args (s/cat :s (s/or :type ::service-type-gql :nil nil?))
  :ret (s/or :type ::service-type :nil nil?))

(defn service-type->gql
  [o]
  (when (keyword? o)
    (str/replace (name o) #"-" "_")))

(s/fdef service-type->gql
  :args (s/cat :o (s/or :type ::service-type :nil nil?))
  :ret (s/or :type ::service-type-gql :nil nil?))

(defn sleep-position->clj
  [s]
  (when-not (str/blank? s)
    (keyword (str "sleep-position/" s))))

(s/fdef sleep-position->clj
  :args (s/cat :s (s/or :position ::sleep-position-gql :nil nil?))
  :ret (s/or :position ::sleep-position :nil nil?))

(defn sleep-position->gql
  [o]
  (when (keyword? o)
    (name o)))

(s/fdef sleep-position->gql
  :args (s/cat :o (s/or :position ::sleep-position :nil nil?))
  :ret (s/or :position ::sleep-position-gql :nil nil?))

(defn timezone->clj
  [s]
  (when-not (str/blank? s)
    (keyword (str "timezone/" s))))

(s/fdef timezone->clj
  :args (s/cat :s (s/or :timezone ::timezone-gql :nil nil?))
  :ret (s/or :timezone ::timezone :nil nil?))

(defn timezone->gql
  [o]
  (when (keyword? o)
    (name o)))

(s/fdef timezone->gql
  :args (s/cat :o (s/or :timezone ::timezone :nil nil?))
  :ret (s/or :timezone ::timezone-gql :nil nil?))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Entities

(declare anatomy->clj anatomy->gql consult->clj consult->gql recommendation->clj recommendation->gql)

;; Patient

(defn patient->clj
  "Convert the patient to the Clojure format."
  [m]
  (cond-> {}
    (:id m)                        (assoc :patient/id (mc/uuid (:id m)))
    (:key m)                       (assoc :patient/key (:key m))
    (:first_name m)                (assoc :patient/first-name (:first_name m) )
    (:last_name  m)                (assoc :patient/last-name (:last_name m))
    (:dob m)                       (assoc :patient/dob (if (t/date? (:dob m))
                                                         (:dob m)
                                                         (t/from-aws-date (:dob m))))
    (:gender m)                    (assoc :patient/gender (gender->clj (:gender m)))
    (:email m)                     (assoc :patient/email (:email m))
    (:phone_number m)              (assoc :patient/phone-number (:phone_number m))
    (:alt_phone_number m)          (assoc :patient/alt-phone-number (:alt_phone_number m))
    (:timezone m)                  (assoc :patient/timezone (timezone->clj (:timezone m)))
    (:communication_preferences m) (assoc :patient/communication-preferences
                                          (set (mapv communication-channel->clj
                                                     (:communication_preferences m))))
    (:image m)                     (assoc :patient/image (:image m))
    (:organization m)              (assoc :patient/organization (t-o/organization->clj (:organization m)) )
    (:anatomy m)                   (assoc :patient/anatomy (anatomy->clj (:anatomy m)))
    ;; (:recommendations m)           (assoc :patient/recommendations (mapv recommendation->clj (:recommendations m)))
    (:last_consult m)              (assoc :patient/last-consult (consult->clj (:last_consult m)))
    (:mask m)                      (assoc :patient/mask (t-c/model->clj (:mask m)))
    (:selector_type m)             (assoc :patient/selector-type (selector-type->clj (:selector_type m)))
    (:selector m)                  (assoc :patient/selector (t-o/user->clj (:selector m)))
    (:communications m)            (assoc :communication/_recipient (mapv t-com/communication->clj (:communications m)))))

(defn patient->gql
  "Convert the patient to the GraphQL format."
  [m]
  (cond-> {}
    (:patient/id m)                        (assoc :id (str (:patient/id m)))
    (:patient/key m)                       (assoc :key (:patient/key m))
    (:patient/first-name m)                (assoc :first_name (:patient/first-name m) )
    (:patient/last-name  m)                (assoc :last_name (:patient/last-name m))
    (:patient/dob m)                       (assoc :dob (if (t/date? (:patient/dob m))
                                                         (t/to-aws-date (:patient/dob m))
                                                         (:patient/dob m)))
    (:patient/gender m)                    (assoc :gender (gender->gql (:patient/gender m)))
    (seq (:patient/email m))               (assoc :email (:patient/email m))
    (seq (:patient/phone-number m))        (assoc :phone_number (:patient/phone-number m))
    (seq (:patient/alt-phone-number m))    (assoc :alt_phone_number (:patient/alt-phone-number m))
    (:patient/timezone m)                  (assoc :timezone (timezone->gql (:patient/timezone m)))
    (:patient/communication-preferences m) (assoc :communication_preferences
                                                  (mapv communication-channel->gql
                                                        (:patient/communication-preferences m)))
    (:patient/image m)                     (assoc :image (:patient/image m))
    (:patient/organization m)              (assoc :organization (t-o/organization->gql (:patient/organization m)))
    (:patient/anatomy m)                   (assoc :anatomy (anatomy->gql (:patient/anatomy m)))
    ;; (:patient/recommendations m)           (assoc :recommendations (mapv recommendation->gql (:patient/recommendations m)))
    (:patient/mask m)                      (assoc :mask (t-c/model->gql (:patient/mask m)))
    (:patient/selector-type m)             (assoc :selector_type (selector-type->gql (:patient/selector-type m)))
    (:patient/selector m)                  (assoc :selector (t-o/user->gql (:patient/selector m)))
    (:communication/_recipient m)          (assoc :communications (mapv t-com/communication->gql (:communication/_recipient m)))))

(defn patient-with-last-consult->clj
  "Convert the patient with the last consult to the Clojure format."
  [m]
  (cond-> {}
    true              (merge (patient->clj m))
    (:last_consult m) (assoc :patient/last-consult (consult->clj (:last_consult m)))))

(defn patient-with-last-consult->gql
  "Convert the patient with the last consult to the GraphQL format."
  [m]
  (cond-> {}
    true                      (merge (patient->gql m))
    (:patient/last-consult m) (assoc :last_consult (consult->gql (:patient/last-consult m)))))

(defn patient-with-consults->clj
  "Convert the patient with consults to the Clojure format."
  [m]
  (cond-> {}
    true              (merge (patient-with-last-consult->clj m))
    (:consults m)     (assoc :consult/_patient (mapv consult->clj (:consults m)))))

(defn patient-with-consults->gql
  "Convert the patient with the consults to the GraphQL format."
  [m]
  (cond-> {}
    true                      (merge (patient-with-last-consult->gql m))
    (:consult/_patient m)     (assoc :consults (mapv consult->gql (:consult/_patient m)))))

(defn patient-consult->clj
  "Convert the patient-consult to the Clojure format."
  [m]
  (cond-> {}
    (:id m)                           (assoc :consult/id (mc/uuid (:id m)))
    (:key m)                          (assoc :consult/key (:key m))
    (:service_type m)                 (assoc :consult/service-type (service-type->clj (:service_type m)))
    (some? (:mask_was_comfortable m)) (assoc :consult/mask-was-comfortable? (:mask_was_comfortable m))
    (some? (:has_portrait m))         (assoc :consult/has-portrait? (:has_portrait m))
    (:status m)                       (assoc :consult/status (consult-state->clj (:status m)))))

(defn patient-consult->gql
  "Convert the patient-consult to the GraphQL format."
  [m]
  (cond-> {}
    (:consult/id m)                            (assoc :id (str (:consult/id m)))
    (:consult/key m)                           (assoc :key (:consult/key m))
    (:consult/service-type m)                  (assoc :service_type (service-type->gql (:consult/service-type m)))
    (some? (:consult/mask-was-comfortable? m)) (assoc :mask_was_comfortable (:consult/mask-was-comfortable? m))
    (some? (:consult/has-portrait? m))         (assoc :has_portrait (:consult/has-portrait? m))
    (:consult/status m)                        (assoc :status (consult-state->gql (:consult/status m)))))

;; Consult

(defn consult->clj
  "Convert the consult to the Clojure format."
  [m]
  (cond-> {}
    (:id m)                           (assoc :consult/id (mc/uuid (:id m)))
    (:key m)                          (assoc :consult/key (:key m))
    (:consented_at m)                 (assoc :consult/consented-at (t/from-aws-date-time (:consented_at m)))
    (:service_type m)                 (assoc :consult/service-type (service-type->clj (:service_type m)))
    (:weight m)                       (assoc :consult/weight (:weight m))
    (:ahi m)                          (assoc :consult/ahi (float (:ahi m)))
    (:modality m)                     (assoc :consult/modality (modality->clj (:modality m)))
    (:cpap m)                         (assoc :consult/cpap (:cpap m))
    (:ipap m)                         (assoc :consult/ipap (:ipap m))
    (:epap m)                         (assoc :consult/epap (:epap m))
    (:manufacturer m)                 (assoc :consult/manufacturer (t-c/manufacturer->clj (:manufacturer m)))
    (:make m)                         (assoc :consult/make (t-c/make->clj (:make m)))
    (:model m)                        (assoc :consult/model (t-c/model->clj (:model m)))
    (some? (:mask_was_comfortable m)) (assoc :consult/mask-was-comfortable? (:mask_was_comfortable m))
    (some? (:take_dentures_out m))    (assoc :consult/take-dentures-out? (:take_dentures_out m))
    true                              (assoc :consult/sleep-positions (set (mapv sleep-position->clj (:sleep_positions m))))
    (:mouth_breathing_level m)        (assoc :consult/mouth-breathing-level (:mouth_breathing_level m))
    (some? (:eye_dryness  m))         (assoc :consult/eye-dryness? (:eye_dryness m))
    (:claustrophobia_level m)         (assoc :consult/claustrophobia-level (:claustrophobia_level m))
    true                              (assoc :consult/issues (set (mapv issue->clj (:issues m))))
    (some? (:deviated_septum m))      (assoc :consult/deviated-septum? (:deviated_septum m))
    (some? (:chronic_issues m))       (assoc :consult/chronic-issues? (:chronic_issues m))
    (some? (:pacemaker m))            (assoc :consult/pacemaker? (:pacemaker m))
    (some? (:sleep_motion m))         (assoc :consult/sleep-motion? (:sleep_motion m))
    (:recommendations m)              (assoc :consult/recommendations (mapv recommendation->clj (:recommendations m)))
    (:recommendations_note m)         (assoc :consult/recommendations-note (:recommendations_note m))
    (:mask m)                         (assoc :consult/mask (t-c/model->clj (:mask m)))
    (:sized_mask m)                   (assoc :consult/sized-mask (t-c/model->clj (:sized_mask m)))
    (:selector_type m)                (assoc :consult/selector-type (selector-type->clj (:selector_type m)))
    (:selector m)                     (assoc :consult/selector (t-o/user->clj (:selector m)))
    (:code m)                         (assoc :consult/code (:code m))
    (:status m)                       (assoc :consult/status (consult-state->clj (:status m)))
    (:recorded_at m)                  (assoc :consult/recorded-at (t/from-aws-date-time (:recorded_at m)))
    (:user m)                         (assoc :consult/user (t-o/user->clj (:user m)))))

(defn consult->gql
  "Convert the consult to the GraphQL format."
  [m]
  (cond-> {}
    (:consult/id m)                            (assoc :id (str (:consult/id m)))
    (:consult/key m)                           (assoc :key (:consult/key m))
    (:consult/consented-at m)                  (assoc :consented_at (t/to-aws-date-time (:consult/consented-at m)))
    (:consult/service-type m)                  (assoc :service_type (service-type->gql (:consult/service-type m)))
    (:consult/weight m)                        (assoc :weight (:consult/weight m))
    (:consult/ahi m)                           (assoc :ahi (float (:consult/ahi m)))
    (:consult/modality m)                      (assoc :modality (modality->gql (:consult/modality m)))
    (:consult/cpap m)                          (assoc :cpap (:consult/cpap m))
    (:consult/ipap m)                          (assoc :ipap (:consult/ipap m))
    (:consult/epap m)                          (assoc :epap (:consult/epap m))
    (:consult/manufacturer m)                  (assoc :manufacturer (t-c/manufacturer->gql (:consult/manufacturer m)))
    (:consult/make m)                          (assoc :make (t-c/make->gql (:consult/make m)))
    (:consult/model m)                         (assoc :model (t-c/model->gql (:consult/model m)))
    (some? (:consult/mask-was-comfortable? m)) (assoc :mask_was_comfortable (:consult/mask-was-comfortable? m))
    (some? (:consult/take-dentures-out? m))    (assoc :take_dentures_out (:consult/take-dentures-out? m))
    true                                       (assoc :sleep_positions (mapv sleep-position->gql (:consult/sleep-positions m)))
    (:consult/mouth-breathing-level m)         (assoc :mouth_breathing_level (:consult/mouth-breathing-level m))
    (some? (:consult/eye-dryness?  m))         (assoc :eye_dryness (:consult/eye-dryness? m))
    (:consult/claustrophobia-level m)          (assoc :claustrophobia_level (:consult/claustrophobia-level m))
    true                                       (assoc :issues (mapv issue->gql (:consult/issues m)))
    (some? (:consult/deviated-septum? m))      (assoc :deviated_septum (:consult/deviated-septum? m))
    (some? (:consult/chronic-issues? m))       (assoc :chronic_issues (:consult/chronic-issues? m))
    (some? (:consult/pacemaker? m))            (assoc :pacemaker (:consult/pacemaker? m))
    (some? (:consult/sleep-motion? m))         (assoc :sleep_motion (:consult/sleep-motion? m))
    (:consult/recommendations m)               (assoc :recommendations (mapv recommendation->gql (:consult/recommendations m)))
    (:consult/recommendations-note m)          (assoc :recommendations_note (:consult/recommendations-note m))
    (:consult/mask m)                          (assoc :mask (t-c/model->gql (:consult/mask m)))
    (:consult/sized-mask m)                    (assoc :sized_mask (t-c/model->gql (:consult/sized-mask m)))
    (:consult/selector-type m)                 (assoc :selector_type (selector-type->gql (:consult/selector-type m)))
    (:consult/selector m)                      (assoc :selector (t-o/user->gql (:consult/selector m)))
    (:consult/code m)                          (assoc :code (:consult/code m))
    (:consult/status m)                        (assoc :status (consult-state->gql (:consult/status m)))
    (:consult/recorded-at m)                   (assoc :recorded_at (t/to-aws-date-time (:consult/recorded-at m)))
    (:consult/user m)                          (assoc :user (t-o/user->gql (:consult/user m)))))

(defn consult-with-patient->clj
  "Convert the consult with the patient to the Clojure format."
  [m]
  (cond-> {}
    true         (merge (consult->clj m))
    (:patient m) (assoc :consult/patient (patient->clj (:patient m)))))

(defn consult-with-patient->gql
  "Convert the consult with the patient to the GraphQL format."
  [m]
  (cond-> {}
    true                 (merge (consult->gql m))
    (:consult/patient m) (assoc :patient (patient->gql (:consult/patient m)))))

(defn anatomy->clj
  "Convert the anatomy to the Clojure format."
  [m]
  (cond-> {}
    (:id m)                             (assoc :anatomy/id (mc/uuid (:id m)))
    (:iris_diameter m)                  (assoc :anatomy/iris-diameter (:iris_diameter m))
    (:head_height m)                    (assoc :anatomy/head-height (:head_height m))
    (:head_width m)                     (assoc :anatomy/head-width (:head_width m))
    (:head_circumference m)             (assoc :anatomy/head-circumference (:head_circumference m))
    (:bridge_width m)                   (assoc :anatomy/bridge-width (:bridge_width m))
    (:bridge_depth m)                   (assoc :anatomy/bridge-depth (:bridge_depth m))
    (:bridge_to_chin m)                 (assoc :anatomy/bridge-to-chin (:bridge_to_chin m))
    (:nose_height m)                    (assoc :anatomy/nose-height (:nose_height m))
    (:nose_width m)                     (assoc :anatomy/nose-width (:nose_width m))
    (:nose_length m)                    (assoc :anatomy/nose-length (:nose_length m))
    (:mouth_width m)                    (assoc :anatomy/mouth-width (:mouth_width m))
    (:ear_to_forehead m)                (assoc :anatomy/ear-to-forehead (:ear_to_forehead m))
    (:nare_size m)                      (assoc :anatomy/nare-size (:nare_size m))
    (:clef_size m)                      (assoc :anatomy/clef-size (:clef_size m))
    (:nose_to_clef m)                   (assoc :anatomy/nose-to-clef (:nose_to_clef m))
    (some? (:facial_hair m))            (assoc :anatomy/facial-hair? (:facial_hair m))
    (some? (:eye_glass_indentations m)) (assoc :anatomy/eye-glass-indentations? (:eye_glass_indentations m))))

(defn anatomy->gql
  "Convert the anatomy to the GraphQL format."
  [m]
  (cond-> {}
    (:anatomy/id m)                              (assoc :id (str (:anatomy/id m)))
    (:anatomy/iris-diameter m)                   (assoc :iris_diameter (:anatomy/iris-diameter m))
    (:anatomy/head-height m)                     (assoc :head_height (:anatomy/head-height m))
    (:anatomy/head-width m)                      (assoc :head_width (:anatomy/head-width m))
    (:anatomy/head-circumference m)              (assoc :head_circumference (:anatomy/head-circumference m))
    (:anatomy/bridge-width m)                    (assoc :bridge_width (:anatomy/bridge-width m))
    (:anatomy/bridge-depth m)                    (assoc :bridge_depth (:anatomy/bridge-depth m))
    (:anatomy/bridge-to-chin m)                  (assoc :bridge_to_chin (:anatomy/bridge-to-chin m))
    (:anatomy/nose-height m)                     (assoc :nose_height (:anatomy/nose-height m))
    (:anatomy/nose-width m)                      (assoc :nose_width (:anatomy/nose-width m))
    (:anatomy/nose-length m)                     (assoc :nose_length (:anatomy/nose-length m))
    (:anatomy/mouth-width m)                     (assoc :mouth_width (:anatomy/mouth-width m))
    (:anatomy/ear-to-forehead m)                 (assoc :ear_to_forehead (:anatomy/ear-to-forehead m))
    (:anatomy/nare-size m)                       (assoc :nare_size (:anatomy/nare-size m))
    (:anatomy/clef-size m)                       (assoc :clef_size (:anatomy/clef-size m))
    (:anatomy/nose-to-clef m)                    (assoc :nose_to_clef (:anatomy/nose-to-clef m))
    (some? (:anatomy/facial-hair? m))            (assoc :facial_hair (:anatomy/facial-hair? m))
    (some? (:anatomy/eye-glass-indentations? m)) (assoc :eye_glass_indentations (:anatomy/eye-glass-indentations? m))))

(defn recommendation->clj
  "Convert the recommendation to the Clojure format."
  [m]
  (cond-> {}
    (:id m)             (assoc :recommendation/id (mc/uuid (:id m)))
    (:model m)          (assoc :recommendation/model (t-c/model->clj (:model m)))
    (:confidence_min m) (assoc :recommendation/confidence-min (:confidence_min m))
    (:confidence_max m) (assoc :recommendation/confidence-max (:confidence_max m))
    (:selector_type m)  (assoc :recommendation/selector-type (selector-type->clj (:selector_type m)))))

(defn recommendation->gql
  "Convert the recommendation to the GraphQL format."
  [m]
  (when (:recommendation/model m)
    (cond-> {}
      (:recommendation/id m)             (assoc :id (str (:recommendation/id m)))
      (:recommendation/model m)          (assoc :model (t-c/model->gql (:recommendation/model m)))
      (:recommendation/confidence-min m) (assoc :confidence_min (:recommendation/confidence-min m))
      (:recommendation/confidence-max m) (assoc :confidence_max (:recommendation/confidence-max m))
      (:recommendation/selector-type m)  (assoc :selector_type (selector-type->gql (:recommendation/selector-type m))))))

;; State <-> DB

(defn- determine-service-type
  [patient]
  (let [exchange-cut-off    (t/plus (t/now) (t/days config/EXCHANGE-DAYS))
        last-consult-status (get-in patient [:patient/last-consult :consult/status])
        recorded-at         (get-in patient [:patient/last-consult :consult/recorded-at])]
    (cond
      ;; prior completed consult was less than 45 days ago
      (and (= :consult-state/completed last-consult-status)
           (t/before? exchange-cut-off recorded-at))
      :service-type/exchange
      ;; prior completed consult was more than 45 days ago
      (and (= :consult-state/completed last-consult-status)
           (t/before? recorded-at exchange-cut-off))
      :service-type/refit
      :else
      ;; :service-type/new-setup ???
      (get-in patient [:patient/last-consult :consult/service-type]))))

(defn- determine-mask-was-comfortable?
  [patient]
  (let [exchange-cut-off    (t/plus (t/now) (t/days config/EXCHANGE-DAYS))
        last-consult-status (get-in patient [:patient/last-consult :consult/status])
        recorded-at         (get-in patient [:patient/last-consult :consult/recorded-at])]
    (cond
      ;; prior completed consult was less than 45 days ago
      (and (= :consult-state/completed last-consult-status)
           (t/before? exchange-cut-off recorded-at))
      false
      ;; prior completed consult was more than 45 days ago
      (and (= :consult-state/completed last-consult-status)
           (t/before? recorded-at exchange-cut-off))
      true
      :else
      ;; true ???
      (get-in patient [:patient/last-consult :consult/mask-was-comfortable?]))))

(defn- determine-current-manufacturer
  [masks mask]
  (str (:manufacturer/id (catalog/lookup-mask-by-model-id masks (:model/id mask)))))

(defn- determine-current-make
  [masks mask]
  (str (:make/id (catalog/lookup-mask-by-model-id masks (:model/id mask)))))

(defn- determine-current-model
  [_masks mask]
  (str (:model/id mask)))

(defn recommendations-with-note->state
  [recommendations-with-note]
  (cond-> {}
    (:recommendations recommendations-with-note)
    (assoc-in [:recommendations :system] (mapv (fn [r] (assoc r :recommendation/model (get-in r [:recommendation/model :model/id])))
                                               (:recommendations recommendations-with-note)))
    (:recommendations-note recommendations-with-note)
    (assoc-in [:recommendations :note] (:recommendations-note recommendations-with-note))))

(defn patient->state
  "Convert the (current) patient with his/her current consult to state format."
  [masks patient]
  (cond-> {}
    (:patient/id patient)
    (assoc-in [:patient :patient/id]
              {:value    (:patient/id patient)
               :dirty?   false
               :touched? false})
    (:patient/key patient)
    (assoc-in [:patient :patient/key]
              {:value    (:patient/key patient)
               :dirty?   false
               :touched? false})
    (:patient/first-name patient)
    (assoc-in [:patient :patient/first-name]
              {:value    (:patient/first-name patient)
               :dirty?   false
               :touched? false})
    (:patient/last-name patient)
    (assoc-in [:patient :patient/last-name]
              {:value    (:patient/last-name patient)
               :dirty?   false
               :touched? false})
    (:patient/dob patient)
    (assoc-in [:patient :patient/dob]
              {:value    (t/to-aws-date (:patient/dob patient))
               :dirty?   false
               :touched? false})
    (:patient/gender patient)
    (assoc-in [:patient :patient/gender]
              {:value    (:patient/gender patient)
               :dirty?   false
               :touched? false})
    (:patient/email patient)
    (assoc-in [:patient :patient/email]
              {:value    (:patient/email patient)
               :dirty?   false
               :touched? false})
    (:patient/phone-number patient)
    (assoc-in [:patient :patient/phone-number]
              {:value    (:patient/phone-number patient)
               :dirty?   false
               :touched? false})
    (:patient/alt-phone-number patient)
    (assoc-in [:patient :patient/alt-phone-number]
              {:value    (:patient/alt-phone-number patient)
               :dirty?   false
               :touched? false})
    (:patient/timezone patient)
    (assoc-in [:patient :patient/timezone]
              {:value    (:patient/timezone patient)
               :dirty?   false
               :touched? false})
    (:patient/communication-preferences patient)
    (assoc-in [:patient :patient/communication-preferences]
              {:value    (:patient/communication-preferences patient)
               :dirty?   false
               :touched? false})
    (:patient/image patient)
    (assoc-in [:patient :patient/image]
              {:value    (:patient/image patient)
               :dirty?   false
               :touched? false})
    ;; current mask
    (:patient/mask patient)
    (assoc-in [:patient :patient/mask]
              {:value    (:patient/mask patient)
               :dirty?   false
               :touched? false})
    (:patient/sized-mask patient)
    (assoc-in [:patient :patient/sized-mask]
              {:value    (:patient/mask patient)
               :dirty?   false
               :touched? false})
    (:patient/selector patient)
    (assoc-in [:patient :patient/selector]
              {:value    (:patient/selector patient)
               :dirty?   false
               :touched? false})
    (:patient/selector-type patient)
    (assoc-in [:patient :patient/selector-type]
              {:value    (:patient/selector-type patient)
               :dirty?   false
               :touched? false})
    ;; anatomy
    (get-in patient [:patient/anatomy :anatomy/id])
    (assoc-in [:anatomy :anatomy/id] (get-in patient [:patient/anatomy :anatomy/id]))
    (get-in patient [:patient/anatomy :anatomy/iris-diameter])
    (assoc-in [:anatomy :anatomy/iris-diameter] (get-in patient [:patient/anatomy :anatomy/iris-diameter]))
    (get-in patient [:patient/anatomy :anatomy/head-height])
    (assoc-in [:anatomy :anatomy/head-height] (get-in patient [:patient/anatomy :anatomy/head-height]))
    (get-in patient [:patient/anatomy :anatomy/head-width])
    (assoc-in [:anatomy :anatomy/head-width] (get-in patient [:patient/anatomy :anatomy/head-width]))
    (get-in patient [:patient/anatomy :anatomy/bridge-width])
    (assoc-in [:anatomy :anatomy/bridge-width] (get-in patient [:patient/anatomy :anatomy/bridge-width]))
    (get-in patient [:patient/anatomy :anatomy/bridge-depth])
    (assoc-in [:anatomy :anatomy/bridge-depth] (get-in patient [:patient/anatomy :anatomy/bridge-depth]))
    (get-in patient [:patient/anatomy :anatomy/bridge-to-chin])
    (assoc-in [:anatomy :anatomy/bridge-to-chin] (get-in patient [:patient/anatomy :anatomy/bridge-to-chin]))
    (get-in patient [:patient/anatomy :anatomy/nose-height])
    (assoc-in [:anatomy :anatomy/nose-height] (get-in patient [:patient/anatomy :anatomy/nose-height]))
    (get-in patient [:patient/anatomy :anatomy/nose-width])
    (assoc-in [:anatomy :anatomy/nose-width] (get-in patient [:patient/anatomy :anatomy/nose-width]))
    (get-in patient [:patient/anatomy :anatomy/nose-length])
    (assoc-in [:anatomy :anatomy/nose-length] (get-in patient [:patient/anatomy :anatomy/nose-length]))
    (get-in patient [:patient/anatomy :anatomy/mouth-width])
    (assoc-in [:anatomy :anatomy/mouth-width] (get-in patient [:patient/anatomy :anatomy/mouth-width]))
    (get-in patient [:patient/anatomy :anatomy/ear-to-forehead])
    (assoc-in [:anatomy :anatomy/ear-to-forehead] (get-in patient [:patient/anatomy :anatomy/ear-to-forehead]))
    (get-in patient [:patient/anatomy :anatomy/nare-size])
    (assoc-in [:anatomy :anatomy/nare-size] (get-in patient [:patient/anatomy :anatomy/nare-size]))
    (get-in patient [:patient/anatomy :anatomy/clef-size])
    (assoc-in [:anatomy :anatomy/clef-size] (get-in patient [:patient/anatomy :anatomy/clef-size]))
    (get-in patient [:patient/anatomy :anatomy/nose-to-clef])
    (assoc-in [:anatomy :anatomy/nose-to-clef] (get-in patient [:patient/anatomy :anatomy/nose-to-clef]))
    (get-in patient [:patient/anatomy :anatomy/head-circumference])
    (assoc-in [:anatomy :anatomy/head-circumference] (get-in patient [:patient/anatomy :anatomy/head-circumference]))
    (some? (get-in patient [:patient/anatomy :anatomy/facial-hair?]))
    (assoc-in [:anatomy :anatomy/facial-hair?] (get-in patient [:patient/anatomy :anatomy/facial-hair?]))
    (some? (get-in patient [:patient/anatomy :anatomy/eye-glass-indentations?]))
    (assoc-in [:anatomy :anatomy/eye-glass-indentations?] (get-in patient [:patient/anatomy :anatomy/eye-glass-indentations?]))
    ;; last consult
    (get-in patient [:patient/last-consult :consult/id])
    (assoc-in [:patient :patient/last-consult :consult/id]
              {:value    (get-in patient [:patient/last-consult :consult/id])
               :dirty?   false
               :touched? false})
    (get-in patient [:patient/last-consult :consult/service-type])
    (assoc-in [:patient :patient/last-consult :consult/service-type]
              {:value    (determine-service-type patient)
               :dirty?   false
               :touched? false})
    (get-in patient [:patient/last-consult :consult/manufacturer])
    (assoc-in [:patient :patient/last-consult :consult/manufacturer]
              {:value    (determine-current-manufacturer masks (get-in patient [:patient/last-consult :consult/mask]))
               :dirty?   false
               :touched? false})
    (get-in patient [:patient/last-consult :consult/make])
    (assoc-in [:patient :patient/last-consult :consult/make]
              {:value    (determine-current-make masks (get-in patient [:patient/last-consult :consult/mask]))
               :dirty?   false
               :touched? false})
    (get-in patient [:patient/last-consult :consult/mask])
    (assoc-in [:patient :patient/last-consult :consult/model]
              {:value    (determine-current-model masks (get-in patient [:patient/last-consult :consult/mask]))
               :dirty?   false
               :touched? false})
    (some? (get-in patient [:patient/last-consult :consult/mask-was-comfortable?]))
    (assoc-in [:patient :patient/last-consult :consult/mask-was-comfortable?]
              {:value    (determine-mask-was-comfortable? patient)
               :dirty?   false
               :touched? false})
    (get-in patient [:patient/last-consult :consult/ahi])
    (assoc-in [:patient :patient/last-consult :consult/ahi]
              {:value    (int (get-in patient [:patient/last-consult :consult/ahi]))
               :dirty?   false
               :touched? false})
    (get-in patient [:patient/last-consult :consult/modality])
    (assoc-in [:patient :patient/last-consult :consult/modality]
              {:value    (get-in patient [:patient/last-consult :consult/modality])
               :dirty?   false
               :touched? false})
    (get-in patient [:patient/last-consult :consult/cpap])
    (assoc-in [:patient :patient/last-consult :consult/cpap]
              {:value    (get-in patient [:patient/last-consult :consult/cpap])
               :dirty?   false
               :touched? false})
    (get-in patient [:patient/last-consult :consult/ipap])
    (assoc-in [:patient :patient/last-consult :consult/ipap]
              {:value    (get-in patient [:patient/last-consult :consult/ipap])
               :dirty?   false
               :touched? false})
    (get-in patient [:patient/last-consult :consult/epap])
    (assoc-in [:patient :patient/last-consult :consult/epap]
              {:value    (get-in patient [:patient/last-consult :consult/epap])
               :dirty?   false
               :touched? false})
    ;; patient submitted data
    (get-in patient [:patient/last-consult :consult/consented-at])
    (assoc-in [:patient :patient/last-consult :consult/consented-at]
              {:value    (get-in patient [:patient/last-consult :consult/consented-at])
               :dirty?   false
               :touched? false})
    (get-in patient [:patient/last-consult :consult/weight])
    (assoc-in [:patient :patient/last-consult :consult/weight]
              {:value    (get-in patient [:patient/last-consult :consult/weight])
               :dirty?   false
               :touched? false})
    (some? (get-in patient [:patient/last-consult :consult/take-dentures-out?]))
    (assoc-in [:patient :patient/last-consult :consult/take-dentures-out?]
              {:value    (get-in patient [:patient/last-consult :consult/take-dentures-out?])
               :dirty?   false
               :touched? false})
    (get-in patient [:patient/last-consult :consult/sleep-positions])
    (assoc-in [:patient :patient/last-consult :consult/sleep-positions]
              {:value    (get-in patient [:patient/last-consult :consult/sleep-positions])
               :dirty?   false
               :touched? false})
    (get-in patient [:patient/last-consult :consult/mouth-breathing-level])
    (assoc-in [:patient :patient/last-consult :consult/mouth-breathing-level]
              {:value    (get-in patient [:patient/last-consult :consult/mouth-breathing-level])
               :dirty?   false
               :touched? false})
    (some? (get-in patient [:patient/last-consult :consult/eye-dryness?]))
    (assoc-in [:patient :patient/last-consult :consult/eye-dryness?]
              {:value    (get-in patient [:patient/last-consult :consult/eye-dryness?])
               :dirty?   false
               :touched? false})
    (get-in patient [:patient/last-consult :consult/claustrophobia-level])
    (assoc-in [:patient :patient/last-consult :consult/claustrophobia-level]
              {:value    (get-in patient [:patient/last-consult :consult/claustrophobia-level])
               :dirty?   false
               :touched? false})
    (get-in patient [:patient/last-consult :consult/issues])
    (assoc-in [:patient :patient/last-consult :consult/issues]
              {:value    (get-in patient [:patient/last-consult :consult/issues])
               :dirty?   false
               :touched? false})
    (some? (get-in patient [:patient/last-consult :consult/chronic-issues?]))
    (assoc-in [:patient :patient/last-consult :consult/chronic-issues?]
              {:value    (get-in patient [:patient/last-consult :consult/chronic-issues?])
               :dirty?   false
               :touched? false})
    (some? (get-in patient [:patient/last-consult :consult/pacemaker?]))
    (assoc-in [:patient :patient/last-consult :consult/pacemaker?]
              {:value    (get-in patient [:patient/last-consult :consult/pacemaker?])
               :dirty?   false
               :touched? false})
    (some? (get-in patient [:patient/last-consult :consult/sleep-motion?]))
    (assoc-in [:patient :patient/last-consult :consult/sleep-motion?]
              {:value    (get-in patient [:patient/last-consult :consult/sleep-motion?])
               :dirty?   false
               :touched? false})
    (some? (get-in patient [:patient/last-consult :consult/deviated-septum?]))
    (assoc-in [:patient :patient/last-consult :consult/deviated-septum?]
              {:value    (get-in patient [:patient/last-consult :consult/deviated-septum?])
               :dirty?   false
               :touched? false})
    (get-in patient [:patient/last-consult :consult/recommendations])
    (assoc-in [:recommendations :system]
              (mapv (fn [r] (assoc r :recommendation/model (get-in r [:recommendation/model :model/id])))
                    (get-in patient [:patient/last-consult :consult/recommendations])))
    (get-in patient [:patient/last-consult :consult/recommendations-note])
    (assoc-in [:recommendations :note]
              (get-in patient [:patient/last-consult :consult/recommendations-note]))
    (get-in patient [:patient/last-consult :consult/status])
    (assoc-in [:patient :patient/last-consult :consult/status]
              {:value    (get-in patient [:patient/last-consult :consult/status])
               :dirty?   false
               :touched? false})))

(defn patient->db
  "Convert the (current) patient and consult to a db format."
  [state]
  (cond-> {}
    ;; patient/id
    (changed? (get-in state [:patient :patient/id]))
    (assoc :patient/id
           (get-in state [:patient :patient/id :value]))
    ;; patient/key
    (changed? (get-in state [:patient :patient/key]))
    (assoc :patient/key
           (get-in state [:patient :patient/key :value]))
    ;; patient/first-name
    (changed? (get-in state [:patient :patient/first-name]))
    (assoc :patient/first-name
           (get-in state [:patient :patient/first-name :value]))
    ;; patient/last-name
    (changed? (get-in state [:patient :patient/last-name]))
    (assoc :patient/last-name
           (get-in state [:patient :patient/last-name :value]))
    ;; patient/dob
    (changed? (get-in state [:patient :patient/dob]))
    (assoc :patient/dob
           (t/from-aws-date (get-in state [:patient :patient/dob :value])))
    ;; patient/gender
    (changed? (get-in state [:patient :patient/gender]))
    (assoc :patient/gender
           (get-in state [:patient :patient/gender :value]))
    ;; patient/email
    (changed? (get-in state [:patient :patient/email]))
    (assoc :patient/email
           (get-in state [:patient :patient/email :value]))
    ;; patient/phone-number
    (changed? (get-in state [:patient :patient/phone-number]))
    (assoc :patient/phone-number
           (get-in state [:patient :patient/phone-number :value]))
    ;; patient/alt-phone-number
    (changed? (get-in state [:patient :patient/alt-phone-number]))
    (assoc :patient/alt-phone-number
           (get-in state [:patient :patient/alt-phone-number :value]))
    ;; patient/timezone
    (changed? (get-in state [:patient :patient/timezone]))
    (assoc :patient/timezone
           (get-in state [:patient :patient/timezone :value]))
    ;; patient/communication-preferences
    (changed? (get-in state [:patient :patient/communication-preferences]))
    (assoc :patient/communication-preferences
           (get-in state [:patient :patient/communication-preferences :value]))
    ;; patient/image
    (changed? (get-in state [:patient :patient/image]))
    (assoc :patient/image
           (get-in state [:patient :patient/image :value]))
    ;; patient/mask
    (changed? (get-in state [:patient :patient/mask]))
    (assoc :patient/mask
           (get-in state [:patient :patient/mask :value]))
    ;; patient/selector
    (changed? (get-in state [:patient :patient/selector]))
    (assoc :patient/selector
           (get-in state [:patient :patient/selector :value]))
    ;; patient/selector-type
    (changed? (get-in state [:patient :patient/selector-type]))
    (assoc :patient/selector-type
           (get-in state [:patient :patient/selector-type :value]))
    ;; consult/id
    (get-in state [:patient :patient/last-consult :consult/id])
    (assoc-in [:patient/last-consult :consult/id]
              (get-in state [:patient :patient/last-consult :consult/id :value]))
    ;; consult/user
    (get-in state [:patient :patient/last-consult :consult/user :value])
    (assoc-in [:patient/last-consult :consult/user :user/id]
              (get-in state [:patient :patient/last-consult :consult/user :value]))
    ;; consult/consented-at
    (get-in state [:patient :patient/last-consult :consult/consented-at])
    (assoc-in [:patient/last-consult :consult/consented-at]
              (get-in state [:patient :patient/last-consult :consult/consented-at :value]))
    ;; consult/service-type
    (get-in state [:patient :patient/last-consult :consult/service-type])
    (assoc-in [:patient/last-consult :consult/service-type]
              (get-in state [:patient :patient/last-consult :consult/service-type :value]))
    ;; consult/manufacturer
    (changed? (get-in state [:patient :patient/last-consult :consult/manufacturer]))
    (assoc-in [:patient/last-consult :consult/manufacturer]
              {:manufacturer/id (mc/uuid (get-in state [:patient :patient/last-consult :consult/manufacturer :value]))})
    ;; consult/make
    (changed? (get-in state [:patient :patient/last-consult :consult/make]))
    (assoc-in [:patient/last-consult :consult/make]
              {:make/id (mc/uuid (get-in state [:patient :patient/last-consult :consult/make :value]))})
    ;; consult/model
    (changed? (get-in state [:patient :patient/last-consult :consult/model]))
    (assoc-in [:patient/last-consult :consult/model]
              {:model/id (mc/uuid (get-in state [:patient :patient/last-consult :consult/model :value]))})
    ;; consult/mask-was-comfortable?
    (changed? (get-in state [:patient :patient/last-consult :consult/mask-was-comfortable?]))
    (assoc-in [:patient/last-consult :consult/mask-was-comfortable?]
              (get-in state [:patient :patient/last-consult :consult/mask-was-comfortable? :value]))
    ;; consult/ahi
    (changed? (get-in state [:patient :patient/last-consult :consult/ahi]))
    (assoc-in [:patient/last-consult :consult/ahi]
              (float (get-in state [:patient :patient/last-consult :consult/ahi :value])))
    ;; consult/modality
    (changed? (get-in state [:patient :patient/last-consult :consult/modality]))
    (assoc-in [:patient/last-consult :consult/modality]
              (get-in state [:patient :patient/last-consult :consult/modality :value]))
    ;; consult/cpap
    (changed? (get-in state [:patient :patient/last-consult :consult/cpap]))
    (assoc-in [:patient/last-consult :consult/cpap]
              (get-in state [:patient :patient/last-consult :consult/cpap :value]))
    ;; consult/ipap
    (changed? (get-in state [:patient :patient/last-consult :consult/ipap]))
    (assoc-in [:patient/last-consult :consult/ipap]
              (get-in state [:patient :patient/last-consult :consult/ipap :value]))
    ;; consult/epap
    (changed? (get-in state [:patient :patient/last-consult :consult/epap]))
    (assoc-in [:patient/last-consult :consult/epap]
              (get-in state [:patient :patient/last-consult :consult/epap :value]))
    ;; consult/weight
    (changed? (get-in state [:patient :patient/last-consult :consult/weight]))
    (assoc-in [:patient/last-consult :consult/weight]
              (get-in state [:patient :patient/last-consult :consult/weight :value]))
    ;; consult/take-dentures-out?
    (changed? (get-in state [:patient :patient/last-consult :consult/take-dentures-out?]))
    (assoc-in [:patient/last-consult :consult/take-dentures-out?]
              (get-in state [:patient :patient/last-consult :consult/take-dentures-out? :value]))
    ;; consult/sleep-positions
    (changed? (get-in state [:patient :patient/last-consult :consult/sleep-positions]))
    (assoc-in [:patient/last-consult :consult/sleep-positions]
              (get-in state [:patient :patient/last-consult :consult/sleep-positions :value]))
    ;; consult/mouth-breathing-level
    (changed? (get-in state [:patient :patient/last-consult :consult/mouth-breathing-level]))
    (assoc-in [:patient/last-consult :consult/mouth-breathing-level]
              (get-in state [:patient :patient/last-consult :consult/mouth-breathing-level :value]))
    ;; consult/claustrophobia-level
    (changed? (get-in state [:patient :patient/last-consult :consult/claustrophobia-level]))
    (assoc-in [:patient/last-consult :consult/claustrophobia-level]
              (get-in state [:patient :patient/last-consult :consult/claustrophobia-level :value]))
    ;; consult/issues
    (changed? (get-in state [:patient :patient/last-consult :consult/issues]))
    (assoc-in [:patient/last-consult :consult/issues]
              (get-in state [:patient :patient/last-consult :consult/issues :value]))
    ;; consult/chronic-issues?
    (changed? (get-in state [:patient :patient/last-consult :consult/chronic-issues?]))
    (assoc-in [:patient/last-consult :consult/chronic-issues?]
              (get-in state [:patient :patient/last-consult :consult/chronic-issues? :value]))
    ;; consult/pacemaker?
    (changed? (get-in state [:patient :patient/last-consult :consult/pacemaker?]))
    (assoc-in [:patient/last-consult :consult/pacemaker?]
              (get-in state [:patient :patient/last-consult :consult/pacemaker? :value]))
    ;; consult/sleep-motion?
    (changed? (get-in state [:patient :patient/last-consult :consult/sleep-motion?]))
    (assoc-in [:patient/last-consult :consult/sleep-motion?]
              (get-in state [:patient :patient/last-consult :consult/sleep-motion? :value]))
    ;; consult/deviated-septum?
    (changed? (get-in state [:patient :patient/last-consult :consult/deviated-septum?]))
    (assoc-in [:patient/last-consult :consult/deviated-septum?]
              (get-in state [:patient :patient/last-consult :consult/deviated-septum? :value]))))


(defn consult->db
  "Convert the consult to a db format."
  [state]
  (let [consult (get-in state [:patient :patient/last-consult])]
    (cond-> {}
      ;; patient/image
      (changed? (get-in state [:patient :patient/image]))
      (assoc :patient/image
             (get-in state [:patient :patient/image :value]))
      ;; consult/id
      (get-in consult [:consult/id :value])
      (assoc :consult/id
             (get-in consult [:consult/id :value]))
      ;; consult/user
      (get-in consult [:consult/user :value])
      (assoc-in [:consult/user :user/id]
                (get-in consult [:consult/user :value]))
      ;; consult/consented-at
      (get-in consult [:consult/consented-at])
      (assoc :consult/consented-at
             (get-in consult [:consult/consented-at :value]))
      ;; consult/service-type
      (get-in consult [:consult/service-type])
      (assoc :consult/service-type
             (get-in consult [:consult/service-type :value]))
      ;; consult/manufacturer
      (and (changed? (get-in consult [:consult/manufacturer]))
           (seq (get-in consult [:consult/manufacturer :value])))
      (assoc :consult/manufacturer
             {:manufacturer/id (mc/uuid (get-in consult [:consult/manufacturer :value]))})
      ;; consult/make
      (and (changed? (get-in consult [:consult/make]))
           (seq (get-in consult [:consult/make :value])))
      (assoc :consult/make
             {:make/id (mc/uuid (get-in consult [:consult/make :value]))})
      ;; consult/model
      (and (changed? (get-in consult [:consult/model]))
           (seq (get-in consult [:consult/model :value])))
      (assoc :consult/model
             {:model/id (mc/uuid (get-in consult [:consult/model :value]))})
      ;; consult/mask-was-comfortable?
      (changed? (get-in consult [:consult/mask-was-comfortable?]))
      (assoc :consult/mask-was-comfortable?
             (get-in consult [:consult/mask-was-comfortable? :value]))
      ;; consult/ahi
      (changed? (get-in consult [:consult/ahi]))
      (assoc :consult/ahi
             (get-in consult [:consult/ahi :value]))
      ;; consult/modality
      (changed? (get-in consult [:consult/modality]))
      (assoc :consult/modality
             (get-in consult [:consult/modality :value]))
      ;; consult/cpap
      (changed? (get-in consult [:consult/cpap]))
      (assoc :consult/cpap
             (get-in consult [:consult/cpap :value]))
      ;; consult/ipap
      (changed? (get-in consult [:consult/ipap]))
      (assoc :consult/ipap
             (get-in consult [:consult/ipap :value]))
      ;; consult/epap
      (changed? (get-in consult [:consult/epap]))
      (assoc :consult/epap
             (get-in consult [:consult/epap :value]))
      ;; consult/weight
      (changed? (get-in consult [:consult/weight]))
      (assoc :consult/weight
             (get-in consult [:consult/weight :value]))
      ;; consult/take-dentures-out?
      (changed? (get-in consult [:consult/take-dentures-out?]))
      (assoc :consult/take-dentures-out?
             (get-in consult [:consult/take-dentures-out? :value]))
      ;; consult/sleep-positions
      (changed? (get-in consult [:consult/sleep-positions]))
      (assoc :consult/sleep-positions
             (get-in consult [:consult/sleep-positions :value]))
      ;; consult/mouth-breathing-level
      (changed? (get-in consult [:consult/mouth-breathing-level]))
      (assoc :consult/mouth-breathing-level
             (get-in consult [:consult/mouth-breathing-level :value]))
      ;; consult/eye-dryness?
      (changed? (get-in consult [:consult/eye-dryness?]))
      (assoc :consult/eye-dryness?
             (get-in consult [:consult/eye-dryness? :value]))
      ;; consult/claustrophobia-level
      (changed? (get-in consult [:consult/claustrophobia-level]))
      (assoc :consult/claustrophobia-level
             (get-in consult [:consult/claustrophobia-level :value]))
      ;; consult/issues
      (changed? (get-in consult [:consult/issues]))
      (assoc :consult/issues
             (get-in consult [:consult/issues :value]))
      ;; consult/chronic-issues?
      (changed? (get-in consult [:consult/chronic-issues?]))
      (assoc :consult/chronic-issues?
             (get-in consult [:consult/chronic-issues? :value]))
      ;; consult/pacemaker?
      (changed? (get-in consult [:consult/pacemaker?]))
      (assoc :consult/pacemaker?
             (get-in consult [:consult/pacemaker? :value]))
      ;; consult/sleep-motion?
      (changed? (get-in consult [:consult/sleep-motion?]))
      (assoc :consult/sleep-motion?
             (get-in consult [:consult/sleep-motion? :value]))
      ;; consult/deviated-septum?
      (changed? (get-in consult [:consult/deviated-septum?]))
      (assoc :consult/deviated-septum?
             (get-in consult [:consult/deviated-septum? :value])))))

(defn consult->state
  "Convert the (current) consult to state format."
  [patient]
  (let [consult (:patient/last-consult patient)]
    (cond-> {}
      (get patient :patient/image)
      (assoc-in [:patient :patient/image]
                {:value    (get patient :patient/image)
                 :dirty?   false
                 :touched? false})
      (get consult :consult/id)
      (assoc-in [:patient :patient/last-consult :consult/id]
                {:value    (:consult/id consult)
                 :dirty?   false
                 :touched? false})
      (get consult :consult/user)
      (assoc-in [:patient :patient/last-consult :consult/user]
                {:value    (get-in consult [:consult/user :user/id])
                 :dirty?   false
                 :touched? false})
      (get consult :consult/consented-at)
      (assoc-in [:patient :patient/last-consult :consult/consented-at]
                {:value    (:consult/consented-at consult)
                 :dirty?   false
                 :touched? false})
      (get consult :consult/service-type)
      (assoc-in [:patient :patient/last-consult :consult/service-type]
                {:value    (get consult :consult/service-type)
                 :dirty?   false
                 :touched? false})
      (get consult :consult/manufacturer)
      (assoc-in [:patient :patient/last-consult :consult/manufacturer]
                {:value    (str (get-in consult [:consult/manufacturer :manufacturer/id]))
                 :dirty?   false
                 :touched? false})
      (get consult :consult/make)
      (assoc-in [:patient :patient/last-consult :consult/make]
                {:value    (str (get-in consult [:consult/make :make/id]))
                 :dirty?   false
                 :touched? false})
      (get consult :consult/model)
      (assoc-in [:patient :patient/last-consult :consult/model]
                {:value    (str (get-in consult [:consult/model :model/id]))
                 :dirty?   false
                 :touched? false})
      (some? (get consult :consult/mask-was-comfortable?))
      (assoc-in [:patient :patient/last-consult :consult/mask-was-comfortable?]
                {:value    (get consult :consult/mask-was-comfortable?)
                 :dirty?   false
                 :touched? false})
      (get consult :consult/ahi)
      (assoc-in [:patient :patient/last-consult :consult/ahi]
                {:value    (get consult :consult/ahi)
                 :dirty?   false
                 :touched? false})
      (get consult :consult/modality)
      (assoc-in [:patient :patient/last-consult :consult/modality]
                {:value    (get consult :consult/modality)
                 :dirty?   false
                 :touched? false})
      (get consult :consult/cpap)
      (assoc-in [:patient :patient/last-consult :consult/cpap]
                {:value    (get consult :consult/cpap)
                 :dirty?   false
                 :touched? false})
      (get consult :consult/ipap)
      (assoc-in [:patient :patient/last-consult :consult/ipap]
                {:value    (get consult :consult/ipap)
                 :dirty?   false
                 :touched? false})
      (get consult :consult/epap)
      (assoc-in [:patient :patient/last-consult :consult/epap]
                {:value    (get consult :consult/epap)
                 :dirty?   false
                 :touched? false})
      ;; patient submitted data
      (get consult [ :consult/weight])
      (assoc-in [:patient :patient/last-consult :consult/weight]
                {:value    (get consult :consult/weight)
                 :dirty?   false
                 :touched? false})
      (some? (get consult :consult/take-dentures-out?))
      (assoc-in [:patient :patient/last-consult :consult/take-dentures-out?]
                {:value    (get consult :consult/take-dentures-out?)
                 :dirty?   false
                 :touched? false})
      (get consult :consult/sleep-positions)
      (assoc-in [:patient :patient/last-consult :consult/sleep-positions]
                {:value    (get consult :consult/sleep-positions)
                 :dirty?   false
                 :touched? false})
      (get consult :consult/mouth-breathing-level)
      (assoc-in [:patient :patient/last-consult :consult/mouth-breathing-level]
                {:value    (get consult :consult/mouth-breathing-level)
                 :dirty?   false
                 :touched? false})
      (some? (get consult [:patient/last-consult :consult/eye-dryness?]))
      (assoc-in [:patient :patient/last-consult :consult/eye-dryness?]
                {:value    (get consult :consult/eye-dryness?)
                 :dirty?   false
                 :touched? false})
      (get consult :consult/claustrophobia-level)
      (assoc-in [:patient :patient/last-consult :consult/claustrophobia-level]
                {:value    (get consult :consult/claustrophobia-level)
                 :dirty?   false
                 :touched? false})
      (get consult :consult/issues)
      (assoc-in [:patient :patient/last-consult :consult/issues]
                {:value    (get consult :consult/issues)
                 :dirty?   false
                 :touched? false})
      (some? (get consult :consult/chronic-issues?))
      (assoc-in [:patient :patient/last-consult :consult/chronic-issues?]
                {:value    (get consult :consult/chronic-issues?)
                 :dirty?   false
                 :touched? false})
      (some? (get consult :consult/pacemaker?))
      (assoc-in [:patient :patient/last-consult :consult/pacemaker?]
                {:value    (get consult :consult/pacemaker?)
                 :dirty?   false
                 :touched? false})
      (some? (get consult :consult/sleep-motion?))
      (assoc-in [:patient :patient/last-consult :consult/sleep-motion?]
                {:value    (get consult :consult/sleep-motion?)
                 :dirty?   false
                 :touched? false})
      (some? (get consult :consult/deviated-septum?))
      (assoc-in [:patient :patient/last-consult :consult/deviated-septum?]
                {:value    (get consult :consult/deviated-septum?)
                 :dirty?   false
                 :touched? false}))))

(defn recommendation->db
  "Convert the recommendation to the db format."
  [recommendation]
  (cond-> {}
    ;; recommendation/id
    (:recommendation/id recommendation)
    (assoc :recommendation/id (:recommendation/id recommendation))
    ;; recommendation/model
    (:recommendation/model recommendation)
    (assoc :recommendation/model {:model/id (:recommendation/model recommendation)})
    ;; recommendation/confidence-min
    (:recommendation/confidence-min recommendation)
    (assoc :recommendation/confidence-min (:recommendation/confidence-min recommendation))
    ;; recommendation/confidence-max
    (:recommendation/confidence-max recommendation)
    (assoc :recommendation/confidence-max (:recommendation/confidence-max recommendation))))

(defn referral->state
  "Convert the (current) patient with his/her current consult to state format."
  [patient]
  (cond-> {}
    (:patient/first-name patient)
    (assoc-in [:patient :patient/first-name]
              {:value    (:patient/first-name patient)
               :dirty?   false
               :touched? false})
    (:patient/last-name patient)
    (assoc-in [:patient :patient/last-name]
              {:value    (:patient/last-name patient)
               :dirty?   false
               :touched? false})
    (:patient/dob patient)
    (assoc-in [:patient :patient/dob]
              {:value    (t/to-aws-date (:patient/dob patient))
               :dirty?   false
               :touched? false})
    (:patient/gender patient)
    (assoc-in [:patient :patient/gender]
              {:value    (:patient/gender patient)
               :dirty?   false
               :touched? false})
    (:patient/email patient)
    (assoc-in [:patient :patient/email]
              {:value    (:patient/email patient)
               :dirty?   false
               :touched? false})
    (:patient/phone-number patient)
    (assoc-in [:patient :patient/phone-number]
              {:value    (:patient/phone-number patient)
               :dirty?   false
               :touched? false})
    (:patient/alt-phone-number patient)
    (assoc-in [:patient :patient/alt-phone-number]
              {:value    (:patient/alt-phone-number patient)
               :dirty?   false
               :touched? false})
    ;; last consult
    (get-in patient [:patient/last-consult :consult/code])
    (assoc-in [:patient :patient/last-consult :consult/code]
              {:value    (get-in patient [:patient/last-consult :consult/code])
               :dirty?   false
               :touched? false})
    (get-in patient [:patient/last-consult :consult/ahi])
    (assoc-in [:patient :patient/last-consult :consult/ahi]
              {:value    (int (get-in patient [:patient/last-consult :consult/ahi]))
               :dirty?   false
               :touched? false})
    (get-in patient [:patient/last-consult :consult/modality])
    (assoc-in [:patient :patient/last-consult :consult/modality]
              {:value    (get-in patient [:patient/last-consult :consult/modality])
               :dirty?   false
               :touched? false})
    (get-in patient [:patient/last-consult :consult/cpap])
    (assoc-in [:patient :patient/last-consult :consult/cpap]
              {:value    (get-in patient [:patient/last-consult :consult/cpap])
               :dirty?   false
               :touched? false})
    (get-in patient [:patient/last-consult :consult/ipap])
    (assoc-in [:patient :patient/last-consult :consult/ipap]
              {:value    (get-in patient [:patient/last-consult :consult/ipap])
               :dirty?   false
               :touched? false})
    (get-in patient [:patient/last-consult :consult/epap])
    (assoc-in [:patient :patient/last-consult :consult/epap]
              {:value    (get-in patient [:patient/last-consult :consult/epap])
               :dirty?   false
               :touched? false})))

(defn referral->db
  "Convert the (current) referral and consult to a db format."
  [state]
  (cond-> {}
    ;; patient/key
    (changed? (get-in state [:patient :patient/key]))
    (assoc :patient/key
           (get-in state [:patient :patient/key :value]))
    ;; patient/first-name
    (changed? (get-in state [:patient :patient/first-name]))
    (assoc :patient/first-name
           (get-in state [:patient :patient/first-name :value]))
    ;; patient/last-name
    (changed? (get-in state [:patient :patient/last-name]))
    (assoc :patient/last-name
           (get-in state [:patient :patient/last-name :value]))
    ;; patient/dob
    (changed? (get-in state [:patient :patient/dob]))
    (assoc :patient/dob
           (t/from-aws-date (get-in state [:patient :patient/dob :value])))
    ;; patient/gender
    (changed? (get-in state [:patient :patient/gender]))
    (assoc :patient/gender
           (get-in state [:patient :patient/gender :value]))
    ;; patient/email
    (changed? (get-in state [:patient :patient/email]))
    (assoc :patient/email
           (get-in state [:patient :patient/email :value]))
    ;; patient/phone-number
    (changed? (get-in state [:patient :patient/phone-number]))
    (assoc :patient/phone-number
           (get-in state [:patient :patient/phone-number :value]))
    ;; patient/alt-phone-number
    (changed? (get-in state [:patient :patient/alt-phone-number]))
    (assoc :patient/alt-phone-number
           (get-in state [:patient :patient/alt-phone-number :value]))
    ;; consult/code
    (get-in state [:patient :patient/last-consult :consult/code :value])
    (assoc-in [:patient/last-consult :consult/code]
              (get-in state [:patient :patient/last-consult :consult/code :value]))
    ;; consult/user
    (get-in state [:patient :patient/last-consult :consult/user :value])
    (assoc-in [:patient/last-consult :consult/user :user/id]
              (get-in state [:patient :patient/last-consult :consult/user :value]))
    ;; consult/ahi
    (changed? (get-in state [:patient :patient/last-consult :consult/ahi]))
    (assoc-in [:patient/last-consult :consult/ahi]
              (float (get-in state [:patient :patient/last-consult :consult/ahi :value])))
    ;; consult/modality
    (changed? (get-in state [:patient :patient/last-consult :consult/modality]))
    (assoc-in [:patient/last-consult :consult/modality]
              (get-in state [:patient :patient/last-consult :consult/modality :value]))
    ;; consult/cpap
    (changed? (get-in state [:patient :patient/last-consult :consult/cpap]))
    (assoc-in [:patient/last-consult :consult/cpap]
              (get-in state [:patient :patient/last-consult :consult/cpap :value]))
    ;; consult/ipap
    (changed? (get-in state [:patient :patient/last-consult :consult/ipap]))
    (assoc-in [:patient/last-consult :consult/ipap]
              (get-in state [:patient :patient/last-consult :consult/ipap :value]))
    ;; consult/epap
    (changed? (get-in state [:patient :patient/last-consult :consult/epap]))
    (assoc-in [:patient/last-consult :consult/epap]
              (get-in state [:patient :patient/last-consult :consult/epap :value]))))
